import React from 'react';
import PropTypes from 'prop-types';
import { observer, inject } from 'mobx-react';
import _ from 'lodash';
import { orderBy } from 'natural-orderby';

import styled from 'styled-components';

import { Box, Heading, Text } from 'grommet';
import { LinkTop } from 'grommet-icons';

import BookElement from 'app/api/bookElement';
import SectionBreak from 'app/components/BookElements/SectionBreak';
import TableOfContentsEntry from 'app/components/BookElements/TableOfContentsEntry';
import StyledParagraph from 'app/components/Paragraph';
import Anchor from 'app/components/Anchor';

import ScrollIntoViewIfNeeded from 'react-scroll-into-view-if-needed';
const scrollIntoViewOptions = { behavior: 'instant', block: 'start' };

const Entry = StyledParagraph;

function sortEntries(entries) {
  return _.chain(entries)
    .toPairs()
    .thru(entries =>
      orderBy(entries, [entry => entry[0].toUpperCase()], ['asc'])
    )
    .fromPairs()
    .value();
}

function groupEntries(entries) {
  return _.chain(entries)
    .toPairs()
    .groupBy(entry => _.head(entry[0]).toUpperCase())
    .mapValues(_.fromPairs)
    .value();
}

function prepareEntries(entries) {
  return _.chain(entries)
    .thru(sortEntries)
    .thru(groupEntries)
    .value();
}

function EntryLabel({ changeSelection, isSelected, entry, title, children }) {
  return (
    <ScrollIntoViewIfNeeded options={scrollIntoViewOptions} active={isSelected}>
      <Box align="center" justify="between" direction="row" gap="small">
        <Entry key={entry} size="xlarge">
          {children}
        </Entry>
        <Anchor size="xsmall" onClick={() => changeSelection(title)}>
          <Box direction="row" align="center" gap="xsmall">
            Back to Top
            <LinkTop size="small" color="neutral-2" />
          </Box>
        </Anchor>
      </Box>
    </ScrollIntoViewIfNeeded>
  );
}

const GlossaryKey = styled(Text)`
  min-width: 50px;
`;

function renderEntry({ entries, entry, title, changeSelection, isSelected }) {
  return [
    <EntryLabel
      key={entry}
      isSelected={isSelected(title + entry)}
      changeSelection={changeSelection}
      title={title}
    >
      {entry}
    </EntryLabel>,
    ..._.map(sortEntries(entries), (value, key) => (
      <Box key={key} margin={{ left: 'small', bottom: 'medium' }}>
        <GlossaryKey weight="bold">{key}</GlossaryKey>
        <Text>{value}</Text>
      </Box>
    )),
  ];
}

class Glossary extends React.Component {
  state = { selection: '' };
  changeSelection = selection => this.setState({ selection });
  isSelected = selection =>
    _.isEqual(this.state.selection.toLowerCase(), selection.toLowerCase());
  render() {
    const { path, content } = this.props;
    if (BookElement.isNotSelected(path)) return null;
    const { details, ...sections } = content;
    return (
      <React.Fragment>
        <TableOfContentsEntry path={path}>
          <Heading responsive textAlign="center">
            Glossary
          </Heading>
        </TableOfContentsEntry>
        {details && <StyledParagraph size="small">{details}</StyledParagraph>}
        {_.map(sections, (entries, title) => (
          <React.Fragment key={title}>
            <ScrollIntoViewIfNeeded
              options={scrollIntoViewOptions}
              active={this.isSelected(title)}
            >
              <Text as="h3" size="large" weight="bold">
                {title}
              </Text>
            </ScrollIntoViewIfNeeded>
            <Box direction="row" gap="xsmall">
              {_.map(prepareEntries(entries), (__, label) => (
                <Anchor
                  key={'glossary-entry-' + label}
                  size="large"
                  onClick={() => this.changeSelection(title + label)}
                >
                  {label}
                </Anchor>
              ))}
            </Box>
            {_.map(prepareEntries(entries), (entries, entry) =>
              renderEntry({
                entries,
                entry,
                changeSelection: this.changeSelection,
                isSelected: this.isSelected,
                title,
              })
            )}
          </React.Fragment>
        ))}

        <SectionBreak />
      </React.Fragment>
    );
  }
}

Glossary.propTypes = {
  /**
   * content path
   */
  path: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ).isRequired,
  /**
   * glossary entries
   */
  content: PropTypes.object,
};
Glossary.defaultProps = {
  content: {},
};

const Component = inject('AppState')(observer(Glossary));
Component.displayName = 'Glossary';
export const GlossaryComponent = Glossary;
export default Component;



// WEBPACK FOOTER //
// ./src/app/components/BookElements/Glossary.js